/**
 * /camp — le Noam Camp : la démonstration est la preuve.
 * Deux heures, les documents réels du prospect, un monde relié en sortie.
 * Hors menu, accessible via les CTA Home et /pourquoi.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, FileText, ShieldCheck, Clock } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const FR = {
  seo_t: "Noam Camp · Deux heures, vos documents, votre monde relié",
  seo_d: "Pas de slides, pas de démo générique. Apportez cinq documents réels : repartez avec votre monde relié, vos angles morts identifiés et une opportunité notée par Noam.",
  ov: "Noam Camp",
  h1: ["La démonstration", "est la preuve."],
  lead: "Pas de slides. Pas de démo générique. Pas de cycle de vente de six mois. Vous apportez cinq documents réels de votre organisation, et en deux heures Noam construit votre monde relié devant vous.",
  meta: ["2 heures", "Vos données réelles", "Sans engagement"],
  s1_ov: "I · Le principe",
  s1_t: ["On ne vous montre pas Noam.", "On vous montre votre organisation."],
  s1_p: "Une démonstration sur des données fictives prouve seulement que le logiciel fonctionne. Le Noam Camp prouve autre chose : que VOTRE réalité, celle qui dort dans vos contrats, vos registres et vos emails, devient un monde interrogeable. Ce que vous voyez à l'écran, ce sont vos entités, vos relations, vos risques, vos opportunités.",
  s2_ov: "II · Le déroulé",
  s2_t: ["Deux heures,", "cinq étapes."],
  steps: [
    ["0:00", "Cadrage", "Vos dossiers en cours, vos trois questions. Qu'est-ce que votre organisation sait ? Qu'ignore-t-elle ? Qu'est-ce qui change ?"],
    ["0:15", "Connecter votre réalité", "Vos cinq documents entrent dans Noam. Le monde se construit devant vous : entités, relations, provenance de chaque information."],
    ["0:45", "Compréhension", "Ce que Noam sait, ce qu'il suppose, ce qu'il ignore sur vos dossiers. Les questions ouvertes qu'il génère sont souvent celles que personne n'avait posées."],
    ["1:15", "Décision", "Une opportunité réelle de votre pipeline reçoit sa note, le commentaire franc de Noam, et les leviers concrets pour l'améliorer. Croisée avec les signaux externes qui la concernent."],
    ["1:45", "Restitution", "Ce que vous gardez, ce que Noam a marqué UNKNOWN, et la liste honnête de ce qui serait branché ensuite : disponible aujourd'hui ou en préparation."],
  ],
  s3_ov: "III · Ce que vous apportez",
  s3_t: ["Cinq documents.", "C'est tout."],
  s3_lead: "Aucun accès à vos systèmes, aucun export complet, aucune préparation. Cinq documents que vous choisissez :",
  docs: [
    "Un contrat ou un mandat en cours",
    "Un organigramme ou une liste de vos entités et partenaires",
    "Un extrait de votre pipeline ou d'un dossier d'affaires",
    "Un registre de risques, un rapport d'audit ou un contrôle récent",
    "Une décision récente importante : note interne, email, compte rendu",
  ],
  s3_conf_t: "Confidentialité",
  s3_conf: "Hébergement en Suisse. Vos documents restent dans un espace isolé, créé pour la session, supprimé sur simple demande après le Camp. Aucune donnée n'entraîne de modèle.",
  s4_ov: "IV · Ce avec quoi vous repartez",
  takeaways: [
    ["Votre monde relié", "Le graphe de vos entités, contrats et dossiers, avec la provenance de chaque information."],
    ["Vos angles morts", "Ce que Noam a marqué UNKNOWN : les questions que vos documents ne permettent pas encore de trancher."],
    ["Une opportunité notée", "Sa note, le commentaire de Noam et les éléments concrets pour améliorer cette note."],
    ["La suite, honnêtement", "Ce qui serait connecté ensuite, avec son statut réel : disponible ou en préparation. Aucune promesse au-delà."],
  ],
  cta_t: "Réservez votre Camp",
  cta_p: "Deux heures. Cinq documents. Votre monde relié.",
  cta_access: "Initier une demande",
  cta_why: "Les problèmes que Noam résout",
};

const EN = {
  seo_t: "Noam Camp · Two hours, your documents, your connected world",
  seo_d: "No slides, no generic demo. Bring five real documents: leave with your connected world, your blind spots identified and one opportunity scored by Noam.",
  ov: "Noam Camp",
  h1: ["The demonstration", "is the proof."],
  lead: "No slides. No generic demo. No six-month sales cycle. You bring five real documents from your organization, and in two hours Noam builds your connected world in front of you.",
  meta: ["2 hours", "Your real data", "No commitment"],
  s1_ov: "I · The principle",
  s1_t: ["We don't show you Noam.", "We show you your organization."],
  s1_p: "A demo on fictional data only proves the software works. The Noam Camp proves something else: that YOUR reality, the one sleeping in your contracts, registers and emails, becomes a queryable world. What you see on screen are your entities, your relationships, your risks, your opportunities.",
  s2_ov: "II · The session",
  s2_t: ["Two hours,", "five steps."],
  steps: [
    ["0:00", "Framing", "Your live files, your three questions. What does your organization know? What doesn't it know? What is changing?"],
    ["0:15", "Connect Your Reality", "Your five documents enter Noam. The world builds itself in front of you: entities, relationships, the provenance of every piece of information."],
    ["0:45", "Understanding", "What Noam knows, assumes and ignores about your files. The open questions it generates are often the ones nobody had asked."],
    ["1:15", "Decision", "A real opportunity from your pipeline receives its score, Noam's candid commentary, and the concrete levers to improve it. Crossed with the external signals that concern it."],
    ["1:45", "Debrief", "What you keep, what Noam marked UNKNOWN, and the honest list of what would be connected next: available today or in preparation."],
  ],
  s3_ov: "III · What you bring",
  s3_t: ["Five documents.", "That's all."],
  s3_lead: "No access to your systems, no full export, no preparation. Five documents of your choosing:",
  docs: [
    "A current contract or mandate",
    "An org chart or a list of your entities and partners",
    "An extract of your pipeline or of a live deal file",
    "A risk register, an audit report or a recent control",
    "A recent important decision: internal note, email, minutes",
  ],
  s3_conf_t: "Confidentiality",
  s3_conf: "Hosted in Switzerland. Your documents stay in an isolated space created for the session, deleted on simple request after the Camp. No data trains any model.",
  s4_ov: "IV · What you leave with",
  takeaways: [
    ["Your connected world", "The graph of your entities, contracts and files, with the provenance of every piece of information."],
    ["Your blind spots", "What Noam marked UNKNOWN: the questions your documents don't yet allow you to settle."],
    ["One scored opportunity", "Its score, Noam's commentary and the concrete elements to improve that score."],
    ["What comes next, honestly", "What would be connected next, with its real status: available or in preparation. No promise beyond that."],
  ],
  cta_t: "Book your Camp",
  cta_p: "Two hours. Five documents. Your connected world.",
  cta_access: "Initiate a request",
  cta_why: "The problems Noam solves",
};

function SectionHead({ ov, t2, lead }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end mb-12">
      <div className="md:col-span-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{ov}</div>
        <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
          {t2[0]}<br />{t2[1]}
        </h2>
      </div>
      {lead && (
        <div className="md:col-span-6 md:pl-10">
          <p className="text-sm text-zinc-400 max-w-md leading-relaxed">{lead}</p>
        </div>
      )}
    </div>
  );
}

export default function Camp() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const L = en ? EN : FR;
  useSEO({ title: L.seo_t, description: L.seo_d, path: "/camp" });
  return (
    <div className="relative overflow-hidden" data-testid="camp-root">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <img src="/home/news_camp.jpg" alt="" className="noam-kenburns absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/75 to-[#0A0E13]/30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 w-full">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#EF9F27] noam-fade-in">{L.ov}</div>
          <h1 className="noam-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mt-6 noam-fade-in" style={{ animationDelay: "0.12s" }} data-testid="camp-h1">
            {L.h1[0]}<br />{L.h1[1]}
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed noam-fade-in" style={{ animationDelay: "0.25s" }}>{L.lead}</p>
          <div className="mt-8 flex items-center gap-6 flex-wrap noam-fade-in" style={{ animationDelay: "0.35s" }}>
            {L.meta.map((m, i) => (
              <span key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                {i === 0 ? <Clock className="w-3.5 h-3.5 text-[#43C6D8]" /> : i === 1 ? <FileText className="w-3.5 h-3.5 text-[#43C6D8]" /> : <ShieldCheck className="w-3.5 h-3.5 text-[#43C6D8]" />}
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* I · LE PRINCIPE */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <SectionHead ov={L.s1_ov} t2={L.s1_t} />
          <p className="text-sm text-zinc-300 max-w-3xl leading-relaxed" data-testid="camp-principle">{L.s1_p}</p>
        </div>
      </section>

      {/* II · LE DÉROULÉ */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <SectionHead ov={L.s2_ov} t2={L.s2_t} />
          <div className="space-y-px bg-white/5 border border-white/5" data-testid="camp-steps">
            {L.steps.map((s, i) => (
              <div key={i} className="bg-[#0A0E13] px-7 py-6 grid grid-cols-12 gap-4 items-start hover:bg-white/[0.02] transition-colors" data-testid={`camp-step-${i}`}>
                <span className="col-span-2 sm:col-span-1 text-[13px] font-mono text-[#EF9F27]">{s[0]}</span>
                <span className="col-span-10 sm:col-span-3 noam-display text-base font-bold tracking-tight text-white">{s[1]}</span>
                <p className="col-span-12 sm:col-span-8 text-[13px] text-zinc-400 leading-relaxed">{s[2]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* III · CE QUE VOUS APPORTEZ */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <SectionHead ov={L.s3_ov} t2={L.s3_t} lead={L.s3_lead} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <ul className="space-y-px bg-white/5 border border-white/5" data-testid="camp-docs">
                {L.docs.map((d, i) => (
                  <li key={i} className="bg-[#0A0E13] px-6 py-4 flex items-center gap-4 text-sm text-zinc-300">
                    <span className="text-[10px] font-mono text-[#43C6D8]">{String(i + 1).padStart(2, "0")}</span>{d}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="relative border border-white/5 px-6 py-6 h-full overflow-hidden" data-testid="camp-confidentiality">
                <img src="/home/cap_trust.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E13] via-[#0A0E13]/80 to-[#0A0E13]/40" />
                <div className="relative">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#43C6D8]/80">
                  <ShieldCheck className="w-3.5 h-3.5" /> {L.s3_conf_t}
                </div>
                <p className="text-[13px] text-zinc-300 mt-3 leading-relaxed">{L.s3_conf}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IV · CE AVEC QUOI VOUS REPARTEZ */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-10">{L.s4_ov}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5" data-testid="camp-takeaways">
            {L.takeaways.map((tk, i) => (
              <div key={i} className="bg-[#0A0E13] px-6 py-7">
                <h3 className="noam-display text-base font-bold tracking-tight text-white">{tk[0]}</h3>
                <p className="text-[12px] text-zinc-400 mt-3 leading-relaxed">{tk[1]}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 relative border border-white/5 px-8 py-12 sm:py-14 overflow-hidden group" data-testid="camp-cta">
            <img src="/home/cta_demo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/75 to-transparent" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="noam-display text-2xl sm:text-3xl font-bold tracking-tight text-white">{L.cta_t}</h3>
              <p className="text-sm text-zinc-400 mt-2">{L.cta_p}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/pourquoi" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/15 text-zinc-200 text-sm hover:border-white/35 hover:text-white transition-colors" data-testid="camp-cta-why">
                {L.cta_why}
              </Link>
              <Link to="/request-access" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-zinc-900 text-sm font-medium hover:bg-zinc-200 transition-colors" data-testid="camp-cta-access">
                {L.cta_access} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
